import React from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Loader2,
  MoreHorizontal,
} from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { describeSyncStatus } from '../utils/syncStatus';
import { isSyncBlocked, syncBlockedLabel } from '../utils/syncEligibility';
import { describeInvoiceStatus } from '../utils/invoiceStatus';
import {
  ALL_FILTER,
  type ColumnFilterKey,
  type ColumnFilters,
  type DealSort,
  type FilterOption,
  type SortKey,
} from '../utils/dealTableFilters';
import { formatCurrencyCompact } from '../utils/formatCurrency';
import { formatDate, formatStripeAmount } from '../utils/formatStripe';
import type { DealMapping, SyncStatus } from '../types/api';

const HISTORICAL_TOOLTIP =
  'This deal was updated prior to Finrite — check if it already exists in Stripe via linking.';

/**
 * States that need no attention, in either column.
 *
 * These render as quiet text rather than a filled badge. Most rows in a
 * healthy tenant are entirely made of these, and giving each one a solid
 * badge meant the table shouted loudest about the things that were fine —
 * leaving a genuinely overdue or failed row no louder than its neighbours.
 */
const CALM_STATES: ReadonlySet<string> = new Set([
  'paid',
  'synced',
  'linked',
]);

/**
 * A status cell: quiet text when nothing is wrong, a badge when it is.
 *
 * The green check marks the settled states so "Paid" stays distinguishable
 * from "Draft" at a glance — both are unalarming, but only one means the
 * money arrived. Failure carries its own glyph so it survives greyscale and
 * colour-blind viewing rather than relying on red alone.
 *
 * red-600 rather than red-500: as TEXT this must clear WCAG AA at 4.5:1
 * (red-600 is 4.62:1 on our background; red-500 only reaches 3.60:1, which
 * passes for a filled badge's white-on-red but not for coloured text).
 */
/**
 * The status label, optionally linked.
 *
 * `href` turns the label itself into the link rather than adding a separate
 * icon or column — the quote status *is* the contract, so "Signed" is the
 * natural thing to click. The anchor stops click propagation because it sits
 * inside a row that navigates on click: opening the contract should not also
 * open the deal. Rendering an anchor (not a click handler on a span) is what
 * gives it a tab stop, a middle-click, and a copyable address for free.
 *
 * Callers must only pass an `href` they have validated; the backend nulls any
 * value that is not an absolute http(s) URL.
 */
function StatusCell({
  label,
  tone,
  href,
  linkTitle,
}: {
  label: string;
  tone: 'calm' | 'neutral' | 'bad';
  href?: string | null;
  linkTitle?: string;
}) {
  const content = (() => {
    if (tone === 'bad') {
      return (
        <>
          <AlertTriangle className="h-3.5 w-3.5" />
          {label}
        </>
      );
    }
    if (tone === 'calm') {
      return (
        <>
          <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-500" />
          {label}
        </>
      );
    }
    return label;
  })();

  const toneClass =
    tone === 'bad'
      ? 'inline-flex items-center gap-1 text-sm font-medium text-red-600 dark:text-red-400'
      : tone === 'calm'
        ? 'inline-flex items-center gap-1 text-sm text-muted-foreground'
        // Neither settled nor wrong — e.g. a draft invoice, or a deal nobody
        // has synced yet. Present, but not competing for attention.
        : 'text-sm text-muted-foreground';

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={linkTitle}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          toneClass,
          'underline decoration-dotted underline-offset-2 hover:decoration-solid',
          'rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        )}
      >
        {content}
      </a>
    );
  }

  return <span className={toneClass}>{content}</span>;
}

/**
 * A tooltip trigger a keyboard can actually reach.
 *
 * Radix forwards its handlers to whatever child it is given, but a bare
 * `span` has no tab stop — so every tooltip in this table (the failure
 * reason, the warning list, the outstanding amount, the historical note)
 * was mouse-only, and the information in them was unreachable without a
 * pointer. `tabIndex=0` puts them in the tab order; `role="button"` and the
 * label say what focus has landed on, since the visible content is often
 * just an icon. Radix opens the tooltip on focus as well as hover, so no
 * key handler is needed.
 *
 * Stops click propagation because these sit inside a row that navigates on
 * click: reading a tooltip should not also open the deal.
 */
const FocusableTooltipTrigger = React.forwardRef<
  HTMLSpanElement,
  { label: string; className?: string; children: React.ReactNode }
>(({ label, className, children, ...props }, ref) => (
  <span
    ref={ref}
    tabIndex={0}
    role="button"
    aria-label={label}
    className={cn(
      'cursor-default rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
      className,
    )}
    onClick={(e) => e.stopPropagation()}
    {...props}
  >
    {children}
  </span>
));
FocusableTooltipTrigger.displayName = 'FocusableTooltipTrigger';

/** Which tone a status label carries. */
function toneFor(status: string | null | undefined, isBad: boolean): 'calm' | 'neutral' | 'bad' {
  if (isBad) return 'bad';
  return CALM_STATES.has((status ?? '').toLowerCase()) ? 'calm' : 'neutral';
}

/**
 * The quote's end state: the contract is signed and nothing is outstanding.
 *
 * `finrite_latest_quote_status` is a HubSpot enumeration with exactly three
 * options — Draft, Sent, Signed — so Draft and Sent are simply earlier points
 * in the same forward progression, not problems. None of the three warrants
 * the "bad" tone, which is reserved for states someone must act on; an
 * unsigned quote is waiting, not failing.
 */
const SIGNED_QUOTE_STATUS = 'signed';

/**
 * How a quote status reads in the table.
 *
 * Matched case-insensitively, and unknown values fall through to the neutral
 * tone rather than being dropped: the property's options are configured in
 * HubSpot, so a fourth one (a "Declined" or "Expired", say) could appear
 * without a change here. It would render as itself — correct but unstyled —
 * rather than vanishing, and that is the moment to add it above.
 */
function quoteTone(status: string): 'calm' | 'neutral' | 'bad' {
  return status.trim().toLowerCase() === SIGNED_QUOTE_STATUS ? 'calm' : 'neutral';
}

/**
 * Whether something real is attached in Stripe.
 *
 * Covers both shapes: a subscription (synced, or a user-confirmed link), and a
 * pure one-time deal billed as a standalone invoice — whose own sync_status
 * stays "not_synced" because it has no subscription, so the invoice fields are
 * what make it attached.
 */
function isAttached(d: DealMapping): boolean {
  if (d.sync_status === 'synced' || d.sync_status === 'linked') return true;
  return Boolean(
    d.one_off_invoice_id &&
      (d.one_off_invoice_status === 'synced' || d.one_off_invoice_status === 'linked'),
  );
}

/**
 * A pre-onboarding deal that Finrite hasn't yet synced or linked.
 *
 * These rows used to render dimmed. They aren't lesser deals — they simply
 * predate the tenant's onboarding — and dimming read as "inactive" or
 * "ignore me" for what is often the most important thing on the page: real
 * billing that may already exist in Stripe. They now render like any other
 * row, and the distinction is carried by the tooltip alone, which explains
 * the state instead of just muting it.
 */
function isUnlinkedHistorical(d: DealMapping): boolean {
  const settled: SyncStatus[] = ['synced', 'linked'];
  return Boolean(d.is_historical) && !settled.includes(d.sync_status);
}

/**
 * A column header that sorts.
 *
 * The whole label is the button rather than a separate icon: the icon alone is
 * a 14px target, and the header text is what people aim at. The arrow shows
 * only the direction currently applied — a neutral up/down glyph marks the
 * columns that CAN sort, so an unsorted table still advertises the ability.
 *
 * `aria-sort` goes on the `th`, where assistive tech looks for it; the button
 * carries the instruction for what the next click does, since "Amount" alone
 * doesn't say it is actionable.
 */
function SortableHead({
  label,
  sortKey,
  sort,
  onToggle,
}: {
  label: string;
  sortKey: SortKey;
  sort: DealSort | null;
  onToggle: (key: SortKey) => void;
}) {
  const active = sort?.key === sortKey;
  const ascending = active && sort.direction === 'asc';
  const Icon = active ? (ascending ? ArrowUp : ArrowDown) : ArrowUpDown;
  const next = !active ? 'descending' : ascending ? 'unsorted' : 'ascending';
  return (
    <TableHead aria-sort={active ? (ascending ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        aria-label={`${label}: sort ${next}`}
        className={cn(
          '-mx-1 inline-flex items-center gap-1 rounded-sm px-1 py-0.5 font-medium outline-none',
          'hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
          active && 'text-foreground',
        )}
      >
        {label}
        <Icon className={cn('h-3.5 w-3.5', !active && 'opacity-40')} />
      </button>
    </TableHead>
  );
}

/**
 * A column header that filters to one of the values present in the column.
 *
 * The trigger is the header itself — styled borderless so the row still reads
 * as a header rather than a form — and names the active value ("Sync: Failed")
 * instead of replacing the column name with it, so a filtered table never
 * leaves you guessing which column is doing the hiding.
 */
function FilterHead({
  label,
  filterKey,
  value,
  options,
  onChange,
}: {
  label: string;
  filterKey: ColumnFilterKey;
  value: string;
  options: FilterOption[];
  onChange: (key: ColumnFilterKey, value: string) => void;
}) {
  const active = value !== ALL_FILTER;
  const selected = options.find((o) => o.value === value);
  return (
    <TableHead>
      <Select value={value} onValueChange={(next) => onChange(filterKey, next)}>
        <SelectTrigger
          aria-label={`Filter by ${label.toLowerCase()}`}
          className={cn(
            'h-7 w-auto gap-1 border-0 bg-transparent px-1 py-0 font-medium shadow-none',
            active && 'font-semibold text-foreground',
          )}
        >
          <span className="whitespace-nowrap">
            {active ? `${label}: ${selected?.label ?? value}` : label}
          </span>
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </TableHead>
  );
}

interface DealsTableProps {
  deals: DealMapping[];
  onRowClick: (deal: DealMapping) => void;
  onLink: (deal: DealMapping) => void;
  onSync: (deal: DealMapping) => void;
  /** Deal currently syncing, so its row can show progress. */
  syncingDealId?: string | null;
  /** Active sort, or null for the order the API returned. */
  sort: DealSort | null;
  onToggleSort: (key: SortKey) => void;
  filters: ColumnFilters;
  /** Values each filterable column offers, derived from the deals in view. */
  filterOptions: Record<ColumnFilterKey, FilterOption[]>;
  onFilterChange: (key: ColumnFilterKey, value: string) => void;
}

const DealsTable: React.FC<DealsTableProps> = ({
  deals,
  onRowClick,
  onLink,
  onSync,
  syncingDealId,
  sort,
  onToggleSort,
  filters,
  filterOptions,
  onFilterChange,
}) => {
  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            {/* Capped rather than left to auto-layout, which hands ALL the
                table's leftover width to the widest column — Name was taking
                40% of the table for text needing half that, leaving a gulf
                between a deal's name and its figures. Long names still wrap;
                they just stop dictating the layout. */}
            <TableHead className="w-[28%]">Name</TableHead>
            {/* No Company column: the deal name usually carries the customer
                already, so the column mostly restated it while costing width
                on every row. The company remains reachable — DealList's
                search matches company_name, and the deal detail names it. */}
            <SortableHead label="Amount" sortKey="amount" sort={sort} onToggle={onToggleSort} />
            {/* Last modified rather than a closed-won date: HubSpot only
                records the latter directly for portals that enabled stage
                calculations, so it previously had to be inferred from other
                fields and explained in a tooltip. This needs no explanation.
                The exact closed-won timestamp, where it exists, is on the
                deal detail page. */}
            <SortableHead
              label="Last modified"
              sortKey="last_modified"
              sort={sort}
              onToggle={onToggleSort}
            />
            {/* Quote, then Invoice, then Sync: this is the order the deal
                actually moves through, so the row reads left to right as its
                lifecycle. Each is named after the thing it describes rather
                than being another column called some flavour of "Status".
                All three filter, because they are the columns whose values
                repeat across rows — filtering an amount or a name is what the
                sort and the search box are for. */}
            <FilterHead
              label="Quote"
              filterKey="quote"
              value={filters.quote}
              options={filterOptions.quote}
              onChange={onFilterChange}
            />
            <FilterHead
              label="Invoice"
              filterKey="invoice"
              value={filters.invoice}
              options={filterOptions.invoice}
              onChange={onFilterChange}
            />
            <FilterHead
              label="Sync"
              filterKey="sync"
              value={filters.sync}
              options={filterOptions.sync}
              onChange={onFilterChange}
            />
            {/* Narrow and centred: the menu button is the only thing in here,
                and right-aligning it in an auto-width column stranded it
                against the table's edge, far from the row it acts on. */}
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deals.map((deal) => {
            const unlinkedHistorical = isUnlinkedHistorical(deal);
            const name = (
              <span className="font-medium">
                {deal.dealname ?? deal.hubspot_deal_id}
              </span>
            );
            return (
              <TableRow
                key={deal.hubspot_deal_id}
                // Roomier rows than the shared Table default (p-2). Scoped
                // here rather than changed in the primitive, which other
                // tables — including the cramped HubSpot iframe ones — rely
                // on being dense.
                className="cursor-pointer [&>td]:py-3"
                onClick={() => onRowClick(deal)}
              >
                <TableCell>
                  {unlinkedHistorical ? (
                    <Tooltip>
                      {/* No role="button" here, unlike the other triggers: this
                          is the deal's name, and the note is context about the
                          deal rather than an action. tabIndex alone puts it in
                          the tab order, and Radix opens the tooltip on focus. */}
                      <TooltipTrigger asChild>
                        <span
                          tabIndex={0}
                          className="rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                        >
                          {name}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{HISTORICAL_TOOLTIP}</TooltipContent>
                    </Tooltip>
                  ) : (
                    name
                  )}
                </TableCell>
                <TableCell>
                  {deal.amount != null
                    ? formatCurrencyCompact(deal.amount, deal.deal_currency_code ?? undefined)
                    : '—'}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {formatDate(deal.last_modified_at)}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {/* The quote status carries the contract link when both
                      exist — "Signed" is the natural thing to click. But the
                      two are independent HubSpot properties, and a deal is
                      listed on its stage alone: a closed deal can hold a
                      contract and no quote at all. That case still needs a
                      way in, so the link stands on its own rather than
                      leaving the document unreachable behind an em dash. */}
                  {deal.quote_status ? (
                    <StatusCell
                      label={deal.quote_status}
                      tone={quoteTone(deal.quote_status)}
                      href={deal.signed_doc_url}
                      linkTitle={
                        deal.signed_doc_url ? 'Open the signed contract' : undefined
                      }
                    />
                  ) : deal.signed_doc_url ? (
                    <StatusCell
                      label="Contract"
                      // Neutral: a contract on a deal with no quote says
                      // nothing about how far along the quote flow is.
                      tone="neutral"
                      href={deal.signed_doc_url}
                      linkTitle="Open the signed contract"
                    />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const invoice = describeInvoiceStatus(deal);
                      if (!invoice) return <span className="text-muted-foreground">—</span>;
                      const remaining = deal.latest_invoice_amount_remaining;
                      const cell = (
                        <StatusCell
                          label={invoice.label}
                          tone={toneFor(deal.latest_invoice_status, invoice.isBad)}
                        />
                      );
                      // Amount outstanding is the reason to care about an
                      // unpaid invoice, so surface it on hover.
                      return remaining ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <FocusableTooltipTrigger label="Amount outstanding">
                              {cell}
                            </FocusableTooltipTrigger>
                          </TooltipTrigger>
                          <TooltipContent>
                            {formatStripeAmount(remaining, deal.latest_invoice_currency)}{' '}
                            outstanding
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        cell
                      );
                    })()}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const status = describeSyncStatus(deal.sync_status);
                      const cell = (
                        <StatusCell
                          label={status.label}
                          tone={toneFor(deal.sync_status, status.isFailed)}
                        />
                      );
                      // A failed row used to say only "Failed", so the one
                      // question it raises — why? — could only be answered by
                      // opening the deal. The reason is already on the row.
                      //
                      // Falls back to sync_status_message: when a previously
                      // synced subscription can no longer be found, the API
                      // marks the row failed and puts "Stripe subscription not
                      // found" there, leaving the stored error_message
                      // untouched — so reading only error_message left exactly
                      // that case with a red badge and no explanation.
                      const reason = deal.error_message || deal.sync_status_message;
                      return status.isFailed && reason ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <FocusableTooltipTrigger label={`Why ${status.label}`}>
                              {cell}
                            </FocusableTooltipTrigger>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm">
                            {reason}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        cell
                      );
                    })()}
                    {/* Warnings sit with the sync state rather than beside the
                        deal name: they are things that went wrong while
                        syncing, so they belong with the other health signals
                        instead of decorating an identifier. */}
                    {deal.warnings && deal.warnings.length > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <FocusableTooltipTrigger
                            label={
                              deal.warnings.length === 1
                                ? '1 warning'
                                : `${deal.warnings.length} warnings`
                            }
                            className="text-amber-700 dark:text-amber-500"
                          >
                            <AlertTriangle className="h-3.5 w-3.5" />
                          </FocusableTooltipTrigger>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <ul className="space-y-1">
                            {deal.warnings.map((w, i) => (
                              <li key={`${w.code}-${i}`}>{w.title ?? w.code}</li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {deal.stripe_status_error && (
                      // Stripe could not be read this poll. The status badge
                      // still shows the deal's real stored state; this flags
                      // that the LIVE status is stale, with the reason.
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <FocusableTooltipTrigger
                            label="Live Stripe status unavailable"
                            className="text-muted-foreground"
                          >
                            <AlertTriangle className="h-3.5 w-3.5" />
                          </FocusableTooltipTrigger>
                        </TooltipTrigger>
                        <TooltipContent>{deal.stripe_status_error}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {isAttached(deal) ? (
                    // An attached deal has no action left to offer here. Its
                    // HubSpot and Stripe links live on the deal detail page
                    // rather than in the row: the table is for scanning many
                    // deals, and per-row links to other products invite
                    // leaving it one deal at a time.
                    null
                  ) : (() => {
                    const isSyncing = syncingDealId === deal.hubspot_deal_id;
                    // Both actions live behind one menu rather than sitting as
                    // buttons on every unsynced row: the column reserved its
                    // width for the widest label on all rows, including the
                    // many with nothing to offer. The trigger is a real button,
                    // so the menu opens by keyboard and touch as well —
                    // unlike a hover-reveal.
                    return (
                      <div className="flex items-center justify-center">
                        {isSyncing ? (
                          // Progress replaces the trigger while syncing, so the
                          // row can't be told to sync twice.
                          <span className="inline-flex items-center gap-1 px-2 text-sm text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Syncing…
                          </span>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label={`Actions for ${deal.dealname ?? deal.hubspot_deal_id}`}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {/* Disabled rather than hidden for a deal that
                                  can't sync: the list now shows deals that are
                                  merely quoted, and merge survivors, and an
                                  action that silently disappears reads as a
                                  missing feature. The reason sits under the
                                  label — a disabled item takes no pointer
                                  events, so a tooltip or title would never
                                  open — and names the actual reason, which is
                                  not always the deal's stage. */}
                              <DropdownMenuItem
                                disabled={isSyncBlocked(deal)}
                                onSelect={() => onSync(deal)}
                                className="flex-col items-start gap-0"
                              >
                                Sync
                                {isSyncBlocked(deal) && (
                                  <span className="text-xs">{syncBlockedLabel(deal)}</span>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => onLink(deal)}>
                                Link Existing…
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    );
                  })()}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
};

export default DealsTable;
